import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Users, 
  Shield, 
  BarChart3, 
  Zap,
  Bot,
  Brain,
  Workflow,
  Settings,
  Eye,
  MessageSquare,
  UserCheck,
  Calendar,
  FileText,
  TrendingUp,
  Headphones,
  ClipboardList,
  ArrowRight,
  Clock,
  DollarSign,
  Target,
  Layers,
  Lock,
  Building2
} from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const Altam = () => {
  const [weeklyHours, setWeeklyHours] = useState(20);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('client-zero');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  const annualCost = weeklyHours * hourlyRate * 52;

  const avaEmployeeTypes = [
    {
      icon: Headphones,
      name: 'AVA Receptionist',
      description: 'Help respond to inquiries, collect information, route requests, schedule appointments and escalate conversations when human assistance is required.'
    },
    {
      icon: TrendingUp,
      name: 'AVA Lead Agent',
      description: 'Help respond to incoming leads, ask qualification questions, follow up with prospects and move qualified opportunities toward your sales team.'
    },
    {
      icon: MessageSquare,
      name: 'AVA Customer Service Agent',
      description: 'Help answer common questions, classify requests, retrieve approved information and escalate exceptions.'
    },
    {
      icon: ClipboardList,
      name: 'AVA Operations Assistant',
      description: 'Help manage repetitive intake, administrative requests, information collection, reminders and workflow coordination.'
    }
  ];

  const avaWorkflows = [
    { icon: MessageSquare, name: 'Customer Service', description: 'Respond to approved inquiries, retrieve information and escalate complex requests.' },
    { icon: Target, name: 'Lead Qualification', description: 'Capture inquiries, ask qualification questions and route opportunities.' },
    { icon: Calendar, name: 'Appointment Scheduling', description: 'Help customers move from inquiry to scheduled next step.' },
    { icon: FileText, name: 'Client Intake', description: 'Collect required information and initiate defined workflows.' },
    { icon: TrendingUp, name: 'Sales Follow-Up', description: 'Trigger approved follow-up processes based on business rules.' },
    { icon: Brain, name: 'Knowledge Assistance', description: 'Help employees or customers retrieve approved organizational information.' },
    { icon: Workflow, name: 'Workflow Coordination', description: 'Move information and tasks through defined processes.' }
  ];

  const aiEmployeeTypes = [
    { name: 'AVA — Customer & Operations', description: 'Customer interaction, intake, qualification, scheduling and workflow coordination.' },
    { name: 'AI Sales Employee', description: 'Prospecting support, qualification and approved follow-up workflows.' },
    { name: 'AI Operations Employee', description: 'Administrative coordination, reporting and repetitive operational processes.' },
    { name: 'AI Knowledge Employee', description: 'Organizational knowledge retrieval and employee support.' },
    { name: 'AI Project Assistant', description: 'Project coordination, status collection, documentation and workflow support.' }
  ];

  const workforceComponents = [
    { icon: Bot, name: 'AI Employees', description: 'AI workers configured around specific business responsibilities.' },
    { icon: Brain, name: 'Business Knowledge', description: 'Approved information, documentation and data sources AI employees can use when completing tasks.' },
    { icon: Workflow, name: 'Workflow Orchestration', description: 'Rules defining what happens, when it happens and which system or person participates next.' },
    { icon: Layers, name: 'Integrations', description: 'Connections between AI employees and authorized business applications.' },
    { icon: Shield, name: 'Governance & Human Oversight', description: 'Permissions, escalation rules, approval points and activity controls appropriate to each workflow.' }
  ];

  const deploymentSteps = [
    { num: 1, name: 'Discover', description: 'Identify where your organization is losing time, money or opportunities.' },
    { num: 2, name: 'Design', description: 'Map the workflow and determine where AI can create measurable value.' },
    { num: 3, name: 'Configure', description: 'Configure an ALTAM AI employee around the selected responsibility.' },
    { num: 4, name: 'Connect', description: 'Integrate approved business information and systems where appropriate.' },
    { num: 5, name: 'Govern', description: 'Define permissions, human approval points and escalation rules.' },
    { num: 6, name: 'Deploy', description: 'Introduce the AI employee into the selected workflow.' },
    { num: 7, name: 'Measure', description: 'Track business outcomes.' },
    { num: 8, name: 'Expand', description: 'Deploy additional workflows only after value has been demonstrated.' }
  ];

  const enterpriseControls = [
    'Role-based access',
    'Defined AI permissions',
    'Human approval points',
    'Escalation rules',
    'Authorized knowledge sources',
    'Workflow boundaries',
    'Activity visibility',
    'Integration controls',
    'Administrative oversight'
  ];

  const clientZeroIncludes = [
    { title: '1 AVA AI Employee', description: 'Configured around a real business function.' },
    { title: '1 Core Business Workflow', description: 'We identify and automate one repetitive, high-value process.' },
    { title: 'Workflow Discovery & Design', description: 'We map how the work happens today and identify where AVA can help.' },
    { title: 'AI Knowledge Configuration', description: 'AVA is configured with the approved information and instructions required for its role.' },
    { title: 'Business Rules & Human Escalation', description: 'You decide what AVA can handle, what requires approval, and when a person takes over.' },
    { title: 'Agreed Integrations', description: 'Connect AVA with approved business systems within the agreed implementation scope.' },
    { title: 'Testing & Acceptance', description: 'We test the workflow against agreed success criteria before final acceptance.' },
    { title: 'Deployment & Team Onboarding', description: 'We help your team understand how AVA works and how to manage escalations.' },
    { title: '30-Day Optimization Period', description: 'We monitor the initial workflow and refine the configuration based on real-world use.' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1538] via-[#1a2d5c] to-[#0A1538]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#227AFF]/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
              <span className="text-[#227AFF] font-semibold">A Pecaro Consulting Technology</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Your Business Doesn't Need{' '}<br />Another AI Tool.
            </h1>
            <p className="text-3xl sm:text-4xl font-bold text-[#227AFF] mb-8">
              It Needs an AI Workforce.
            </p>
            
            <p className="text-xl text-gray-300 mb-6 max-w-3xl mx-auto">
              <span className="text-white font-semibold">ALTAM</span><span className="text-[#227AFF]">™</span> is an AI workforce platform being built to help businesses deploy, connect and manage AI employees across real business workflows.
            </p>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">
              Instead of adding another disconnected AI application to your technology stack, ALTAM is designed to become the orchestration layer between your people, AI models, business knowledge, workflows and existing systems.
            </p>
            
            <p className="text-xl text-[#227AFF] font-semibold mb-10 italic">
              AI should amplify human potential — not replace it.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => openIntake('meet-ava')} className="btn btn-accent text-lg px-8 inline-flex items-center gap-2">
                <Bot className="h-5 w-5" /> Meet AVA
              </button>
              <Link to="/book-consultation" className="btn btn-glass text-lg px-8">
                Book an ALTAM Strategy Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* From AI Tools to AI Employees */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">From AI Tools to AI Employees</h2>
              <div className="accent-line"></div>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-6">Businesses already have software.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                {['CRM', 'Email', 'Calendars', 'Accounting platforms', 'Customer portals', 'Project management', 'Documents', 'Databases'].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg px-4 py-2 text-center text-gray-600 text-sm font-medium shadow-sm">
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-700 mb-2">And now dozens of AI tools.</p>
              <p className="text-lg text-gray-700 mb-6">The problem isn't access to technology.</p>
              <p className="text-2xl font-bold text-[#0A1538] text-center">The problem is coordination.</p>
            </div>
            
            <div className="space-y-3 mb-8">
              {[
                'Employees still move information between systems.',
                'Customers still wait for responses.',
                'Leads still require manual follow-up.',
                'Managers still chase updates.',
                'Teams still search for information.',
                'Important workflows still depend on repetitive human coordination.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#227AFF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-[#227AFF]/5 rounded-xl p-6 text-center">
              <p className="text-lg text-gray-700">
                ALTAM is being designed to help connect these pieces into an <span className="text-[#227AFF] font-semibold">intelligent workforce layer</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Think Shopify — For AI Employees */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Think Shopify — For AI Employees</h2>
              <div className="accent-line"></div>
            </div>
            
            <p className="text-lg text-gray-700 mb-6 text-center">
              Shopify made it easier for businesses to establish and operate digital commerce without building the entire infrastructure themselves.
            </p>
            <p className="text-lg text-gray-700 mb-8 text-center">
              <span className="font-semibold">ALTAM's vision is similar for AI-powered work.</span>
            </p>
            <p className="text-lg text-gray-600 mb-10 text-center">
              Businesses shouldn't need teams of AI engineers to benefit from intelligent automation. ALTAM is being built to make it easier for organizations to configure and deploy AI employees around the jobs their businesses actually need performed.
            </p>
            
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                'Choose the job.',
                'Connect the knowledge.',
                'Define the workflow.',
                'Set the permissions.',
                'Deploy the AI employee.',
                'Measure the outcome.'
              ].map((step, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#227AFF]">
                  <p className="font-semibold text-gray-800">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet AVA Section */}
      <section id="meet-ava" className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-2 text-white">Meet the First ALTAM AI Employee</h2>
              <p className="text-4xl font-bold text-[#227AFF] mb-4">AVA<span className="text-white">™</span></p>
              <div className="accent-line"></div>
            </div>
            
            <p className="text-lg text-gray-300 text-center mb-10 max-w-3xl mx-auto">
              AVA is the first AI employee powered by ALTAM. AVA can be configured to support customer-facing and internal workflows such as:
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {avaWorkflows.map((workflow, i) => {
                const IconComponent = workflow.icon;
                return (
                  <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-[#227AFF]/20">
                        <IconComponent className="h-5 w-5 text-[#227AFF]" />
                      </div>
                      <h4 className="font-semibold text-white">{workflow.name}</h4>
                    </div>
                    <p className="text-gray-400 text-sm">{workflow.description}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center">
              <button onClick={() => openIntake('meet-ava')} className="btn btn-accent inline-flex items-center gap-2">
                <Bot className="h-5 w-5" /> Meet AVA
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* One Platform. Multiple AI Employees */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">One Platform. Multiple AI Employees.</h2>
              <div className="accent-line"></div>
            </div>
            
            <p className="text-lg text-gray-700 text-center mb-8">
              AVA is the beginning of the ALTAM vision. Over time, organizations may require different AI employees for different responsibilities.
            </p>
            
            <div className="space-y-4 mb-10">
              {aiEmployeeTypes.map((type, i) => (
                <div key={i} className="bg-white rounded-xl p-5 shadow-sm flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#227AFF]/10">
                    <Bot className="h-5 w-5 text-[#227AFF]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{type.name}</h4>
                    <p className="text-gray-600 text-sm">{type.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">The objective is not to create digital people for novelty.</p>
              <p className="text-xl font-semibold text-white">The objective is to assign AI clearly defined business responsibilities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Built Around the Business — Not the Model */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Built Around the Business — Not the Model</h2>
              <div className="accent-line"></div>
            </div>
            
            <p className="text-lg text-gray-700 text-center mb-6">
              AI technology is changing quickly. Today's leading model may not be tomorrow's leading model.
            </p>
            <p className="text-lg text-gray-700 text-center mb-8">
              Businesses shouldn't have to rebuild their operations every time AI technology changes.
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
              <p className="text-lg text-gray-700 mb-6">
                ALTAM's architecture is being designed around a <span className="text-[#227AFF] font-semibold">model-agnostic philosophy</span>.
              </p>
              <p className="text-gray-600 mb-4">Where appropriate and supported, the platform can evolve to work across different AI technologies based on factors such as:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['Use case', 'Capability', 'Cost', 'Performance', 'Privacy requirements', 'Reliability', 'Organizational policy'].map((factor, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-center text-sm text-gray-700">
                    {factor}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-600 mb-2">The AI model becomes part of the infrastructure.</p>
              <p className="text-xl font-semibold text-[#0A1538]">The business workflow remains the priority.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The ALTAM AI Workforce Layer */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">The ALTAM AI Workforce Layer</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">ALTAM's vision brings together five critical components.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {workforceComponents.map((component, i) => {
                const IconComponent = component.icon;
                return (
                  <div key={i} className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-[#227AFF]/10">
                        <IconComponent className="h-6 w-6 text-[#227AFF]" />
                      </div>
                      <h4 className="font-semibold text-gray-800">{component.name}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{component.description}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-lg text-gray-700">
                Together, these components create the foundation for an <span className="text-[#227AFF] font-semibold">AI-enabled workforce</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Humans + AI */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-300 mb-4">We don't believe the future of business is humans versus AI.</p>
            <p className="text-xl text-gray-300 mb-8">We believe it is:</p>
            
            <h2 className="text-5xl font-bold text-white mb-10">Humans + AI.</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-10 text-left">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h4 className="text-[#227AFF] font-semibold mb-4">AI excels at:</h4>
                <p className="text-gray-300">Repetitive processing, information retrieval, pattern recognition, communication support and workflow execution.</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h4 className="text-[#227AFF] font-semibold mb-4">People remain essential for:</h4>
                <p className="text-gray-300">Judgment, accountability, relationships, leadership, creativity, empathy and complex decision-making.</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">ALTAM is being built around that division of strengths.</p>
            
            <div className="space-y-2">
              <p className="text-xl text-white font-semibold">Let AI handle more of the repetition.</p>
              <p className="text-xl text-[#227AFF] font-semibold">Give people more time for what matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Start With One Workflow */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Start With One Workflow</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">AI transformation does not have to begin with a massive technology project.<br />Start with one measurable business problem.</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {deploymentSteps.map((step, i) => (
                <div key={i} className="bg-white rounded-xl p-5 shadow-sm relative overflow-hidden">
                  <div className="absolute -top-2 -left-2 w-12 h-12 bg-[#227AFF]/10 rounded-full flex items-center justify-center">
                    <span className="text-[#227AFF] font-bold text-lg">{step.num}</span>
                  </div>
                  <div className="pt-6">
                    <h4 className="font-semibold text-gray-800 mb-2">{step.name}</h4>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-xl font-semibold text-white">Start small. Prove value. Scale intelligently.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI for Businesses That Don't Have an AI Department */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">AI for Businesses That Don't Have an AI Department</h2>
            <div className="accent-line"></div>
            
            <p className="text-lg text-gray-700 mt-8 mb-4">
              Large organizations can spend millions building AI capabilities.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Most small and mid-sized businesses can't.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              We believe access to practical AI shouldn't depend on having an enormous technology budget.
            </p>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="text-lg text-gray-700">
                ALTAM's long-term mission is to make sophisticated AI workforce capabilities accessible to organizations that want to <span className="text-[#227AFF] font-semibold">work smarter</span> without building everything themselves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Could Your AI Workforce Handle? */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What Could Your AI Workforce Handle?</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">Imagine arriving at work and finding that:</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {[
                'Customer inquiries have already been categorized.',
                'New prospects have already received an initial response.',
                'Qualified leads are ready for your sales team.',
                'Appointments have been scheduled.',
                'Routine questions have been answered using approved information.',
                'Intake information has been collected.',
                'Internal requests have been routed.',
                'Employees can find company information faster.',
                'Exceptions requiring judgment are waiting for the appropriate person.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-gradient-to-r from-[#0A1538] to-[#1a2d5c] rounded-xl p-8 text-center">
              <p className="text-gray-300 mb-4">Your team doesn't start the day buried in repetitive work.</p>
              <p className="text-2xl font-bold text-white mb-4">They start with the work that matters.</p>
              <p className="text-[#227AFF] font-semibold">That is the future ALTAM is being built to enable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Control. Human Oversight. */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Enterprise Control. Human Oversight.</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">AI in business requires more than intelligence. It requires control.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-6">
              <p className="text-gray-700 mb-6">ALTAM's product direction includes capabilities designed around areas such as:</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {enterpriseControls.map((control, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-[#227AFF]" />
                    <span className="text-gray-700 text-sm">{control}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <p className="text-sm text-gray-500 text-center italic">
              Specific capabilities vary by implementation and should be validated against the organization's security, privacy, regulatory and operational requirements.
            </p>
          </div>
        </div>
      </section>

      {/* AVA Client Zero Pricing */}
      <section id="client-zero" className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Put Your First AI Employee to Work</h2>
              <div className="accent-line"></div>
              <p className="text-xl text-gray-600 mt-6">Start with one workflow. Prove the value. Then scale.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
              <p className="text-lg text-gray-700 mb-6">
                Your team shouldn't spend its best hours answering the same questions, chasing the same follow-ups, collecting the same information, or moving data between systems.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                AVA helps your business delegate repetitive work to AI employees — <span className="text-[#227AFF] font-semibold">while your people remain in control</span>.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-600">No massive transformation project.</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-600">No need to automate your entire business.</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-600">Start with one high-value workflow.</p>
                </div>
              </div>
            </div>
            
            {/* Client Zero Card */}
            <div className="bg-gradient-to-br from-[#0A1538] via-[#1a2d5c] to-[#0A1538] rounded-2xl p-8 text-white mb-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#227AFF]/10 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <span className="inline-block bg-[#227AFF] text-white text-sm font-semibold px-4 py-1 rounded-full mb-4">
                    FOUNDING CUSTOMER PROGRAM
                  </span>
                  <h3 className="text-3xl font-bold mb-2">AVA CLIENT ZERO</h3>
                  <p className="text-gray-300">Designed for businesses ready to put their first AVA AI Employee to work.</p>
                </div>
                
                <div className="text-center mb-8">
                  <p className="text-5xl font-bold text-white mb-2">$3,500 <span className="text-xl font-normal text-gray-400">CAD</span></p>
                  <p className="text-gray-300">One-time founding implementation</p>
                  <div className="flex justify-center gap-8 mt-4">
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-[#227AFF]">$1,750</p>
                      <p className="text-sm text-gray-400">to start</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-[#227AFF]">$1,750</p>
                      <p className="text-sm text-gray-400">after acceptance testing</p>
                    </div>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h4 className="text-xl font-semibold mb-6 text-center">Your Client Zero Implementation Includes</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {clientZeroIncludes.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-[#227AFF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-white">{item.title}</p>
                          <p className="text-sm text-gray-400">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
                  <h4 className="font-semibold text-[#227AFF] mb-3">Founding Customer Advantage</h4>
                  <p className="text-gray-300 text-sm">
                    Client Zero participants receive early access to AVA capabilities, direct input into relevant product development, and access to preferential founding-customer pricing when AVA subscription plans become generally available.
                  </p>
                </div>
                
                <div className="text-center">
                  <button 
                    onClick={() => openIntake('client-zero')}
                    className="btn btn-accent text-lg px-10 py-4 inline-flex items-center gap-2"
                    data-testid="apply-client-zero-btn"
                  >
                    Apply for Client Zero <ArrowRight className="h-5 w-5" />
                  </button>
                  <p className="text-gray-400 text-sm mt-4">
                    Limited founding-customer availability. Applications are reviewed to ensure AVA is appropriate for the proposed workflow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Could Your AVA Employee Do? */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What Could Your AVA Employee Do?</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">Your AVA employee can be configured around the work your business repeatedly handles.</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {avaEmployeeTypes.map((type, i) => {
                const IconComponent = type.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-[#227AFF]/10">
                        <IconComponent className="h-6 w-6 text-[#227AFF]" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800">{type.name}</h4>
                    </div>
                    <p className="text-gray-600">{type.description}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">You don't need all four.</p>
              <p className="text-white font-semibold">We start by identifying the one workflow that can create the greatest measurable value for your business.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Start With One Workflow? */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Start With One Workflow?</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">AI transformation doesn't have to begin with a massive technology project.</p>
            </div>
            
            <div className="mb-10">
              <p className="text-center text-lg font-semibold text-gray-700 mb-8">AVA follows a simpler approach:</p>
              <div className="flex flex-wrap justify-center gap-2 mb-10">
                {['DISCOVER', 'DESIGN', 'DEPLOY', 'MEASURE', 'SCALE'].map((step, i) => (
                  <React.Fragment key={i}>
                    <span className="bg-[#227AFF] text-white px-4 py-2 rounded-lg font-semibold">{step}</span>
                    {i < 4 && <ArrowRight className="h-6 w-6 text-gray-400 self-center" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { num: 1, name: 'Discover', desc: 'Identify where repetitive work is consuming time, increasing costs or slowing customers down.' },
                { num: 2, name: 'Design', desc: 'Define the workflow, business rules, systems, approvals and human escalation points.' },
                { num: 3, name: 'Deploy', desc: 'Configure an AVA AI Employee around that specific workflow.' },
                { num: 4, name: 'Measure', desc: 'Track agreed indicators such as response time, processing time, workflow completion and manual effort.' },
                { num: 5, name: 'Scale', desc: 'When AVA demonstrates value, expand into additional workflows and AI employees.' }
              ].map((step, i) => (
                <div key={i} className="bg-white rounded-xl p-5 shadow-sm text-center">
                  <div className="w-10 h-10 bg-[#227AFF] text-white rounded-full flex items-center justify-center font-bold mx-auto mb-3">
                    {step.num}
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">{step.name}</h4>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Humans Stay in Control */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Humans Stay in Control</h2>
              <div className="accent-line"></div>
              <p className="text-xl text-[#227AFF] font-semibold mt-6">AI should amplify human potential — not remove human accountability.</p>
            </div>
            
            <p className="text-lg text-gray-300 text-center mb-8">AVA is designed around human oversight. Your organization determines:</p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {[
                'What AVA can do independently',
                'What requires human approval',
                'When AVA must escalate',
                'What information AVA can access',
                'Which systems AVA can interact with'
              ].map((item, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3 border border-white/10">
                  <Shield className="h-5 w-5 text-[#227AFF] flex-shrink-0" />
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <p className="text-gray-300 mb-2">AVA handles the repetitive work.</p>
              <p className="text-xl font-semibold text-white">Your people handle the decisions that need people.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">How Much Is Repetitive Work Costing You?</h2>
              <div className="accent-line"></div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <p className="text-lg text-gray-700 text-center mb-8">Consider a team spending:</p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <label className="block text-gray-600 mb-2">Hours every week on repetitive work</label>
                  <input 
                    type="number" 
                    min="0"
                    value={weeklyHours}
                    onChange={(e) => setWeeklyHours(Math.max(0, Number(e.target.value) || 0))}
                    className="w-full text-center text-3xl font-bold text-[#227AFF] border-2 border-gray-200 rounded-lg p-3 focus:border-[#227AFF] focus:outline-none"
                  />
                </div>
                <div className="text-center">
                  <label className="block text-gray-600 mb-2">Average labour cost per hour</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-bold text-gray-400">$</span>
                    <input 
                      type="number" 
                      min="0"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full text-center text-3xl font-bold text-[#227AFF] border-2 border-gray-200 rounded-lg p-3 pl-10 focus:border-[#227AFF] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="text-center bg-[#0A1538] rounded-xl p-6">
                  <label className="block text-gray-400 mb-2">Approximate annual cost</label>
                  <p className="text-4xl font-bold text-white">${annualCost.toLocaleString()}</p>
                  <p className="text-gray-400 text-sm mt-1">per year</p>
                </div>
              </div>
              
              <div className="bg-[#227AFF]/5 rounded-xl p-6 text-center">
                <p className="text-gray-700">
                  If automation could responsibly reduce a meaningful portion of that manual work, your team could redirect those hours toward <span className="text-[#227AFF] font-semibold">customers, growth, and problem-solving</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built by Pecaro Consulting */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Built by Pecaro Consulting</h2>
              <div className="accent-line"></div>
            </div>
            
            <p className="text-lg text-gray-700 text-center mb-6">
              ALTAM is developed by <span className="font-semibold">Pecaro Consulting Inc.</span>, combining business transformation, technology delivery and AI automation.
            </p>
            <p className="text-lg text-gray-600 text-center mb-8">
              That matters because successful AI transformation isn't simply a software installation. It requires understanding:
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {['People.', 'Processes.', 'Technology.', 'Risk.', 'Customer experience.', 'Business value.'].map((item, i) => (
                <div key={i} className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <span className="font-semibold text-gray-800">{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-lg text-gray-700 text-center mt-8">
              Pecaro helps organizations <span className="text-[#227AFF] font-semibold">identify where AI belongs</span> before implementing the technology.
            </p>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-2 text-white">Our Vision</h2>
            <p className="text-2xl text-[#227AFF] font-semibold mb-8">The World's Most Accessible AI Employee Platform.</p>
            
            <div className="space-y-4 mb-10 text-left max-w-2xl mx-auto">
              {[
                'We envision a future where organizations of every size can build their own AI-enabled workforce.',
                'Where AI employees work alongside people.',
                'Where repetitive work consumes less human potential.',
                'Where technology adapts to businesses instead of businesses constantly adapting to technology.',
                'Where entrepreneurs can scale without adding administrative complexity at the same rate.',
                'And where AI creates more time for people to focus on customers, creativity, relationships, innovation and life outside of work.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#227AFF] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">{item}</p>
                </div>
              ))}
            </div>
            
            <p className="text-2xl font-bold text-white">ALTAM exists to give time back to the things that matter.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">The AI Workforce Starts With One Employee.</h2>
            <p className="text-4xl font-bold text-[#227AFF] mb-6">Meet AVA.</p>
            
            <p className="text-lg text-gray-700 mb-4">
              AVA is the first AI employee powered by ALTAM.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              And we're inviting selected organizations to help us build the future of AI-powered work through the <span className="font-semibold">AVA Client Zero Program</span>.
            </p>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 mb-10">
              <p className="text-xl text-gray-700 mb-2">Bring us one business problem.</p>
              <p className="text-xl font-semibold text-[#0A1538]">We'll determine whether AVA can help solve it.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => openIntake('meet-ava')} className="btn btn-primary text-lg px-8 inline-flex items-center gap-2">
                <Bot className="h-5 w-5" /> Meet AVA
              </button>
              <button onClick={() => openIntake('client-zero')} className="btn btn-accent text-lg px-8" data-testid="apply-client-zero-btn">
                Apply for Client Zero
              </button>
              <Link to="/book-consultation" className="btn btn-glass text-lg px-8">
                Book a Strategy Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Notice */}
      <section className="bg-[#0A1538] py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-2xl font-bold text-white mb-2">
              ALTAM<span className="text-[#227AFF]">™</span>
            </p>
            <p className="text-[#227AFF] font-semibold mb-4">
              AI that amplifies human potential.
            </p>
            <p className="text-gray-500 text-sm">
              A Pecaro Consulting Inc. technology.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default Altam;
